<template>
    <v-text-field
        ref="field"
        :value="text"
        :label="label"
        :dense="dense"
        :outlined="outlined"
        :disabled="disabled"
        :clearable="clearable"
        :prepend-inner-icon="prependInnerIcon"
        :error-messages="errorMessages"
        :hide-details="hideDetails"
        placeholder="dd/mm/aaaa"
        maxlength="10"
        inputmode="numeric"
        autocomplete="off"
        @input="onInput"
        @blur="onBlur"
        @keydown.enter="onBlur"
    >
        <template v-slot:append>
            <v-menu
                v-model="menu"
                :close-on-content-click="false"
                :disabled="disabled"
                transition="scale-transition"
                offset-y
                left
                min-width="auto"
            >
                <template v-slot:activator="{ on, attrs }">
                    <v-icon v-bind="attrs" v-on="on" :disabled="disabled" style="cursor: pointer">mdi-calendar-today</v-icon>
                </template>
                <v-date-picker :value="isoValue" locale="pt-BR" no-title @input="onPick" />
            </v-menu>
        </template>
    </v-text-field>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

// Campo de data em que o usuario pode digitar (mascara dd/mm/aaaa) ou escolher pelo
// calendario (icone). O valor (v-model) e sempre ISO "aaaa-mm-dd" ou null.
const props = defineProps({
    value: { type: String, default: null },
    label: { type: String, default: null },
    errorMessages: { type: [Array, String], default: undefined },
    prependInnerIcon: { type: String, default: undefined },
    dense: { type: Boolean, default: false },
    outlined: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    clearable: { type: Boolean, default: false },
    hideDetails: { type: [Boolean, String], default: false },
})

const emit = defineEmits(['input'])

const field = ref(null)
const menu = ref(false)

// A API pode devolver "2026-08-01" ou "2026-08-01T00:00:00.000000Z" - so interessa o dia.
function toIso(value) {
    if (!value) return null
    const day = String(value).split(/[ T]/)[0]
    return /^\d{4}-\d{2}-\d{2}$/.test(day) ? day : null
}

function toBr(iso) {
    const day = toIso(iso)
    if (!day) return ''
    const [year, month, date] = day.split('-')
    return `${date}/${month}/${year}`
}

// "dd/mm/aaaa" -> "aaaa-mm-dd" (null se incompleta ou data inexistente, ex.: 31/02).
function brToIso(text) {
    const match = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(text)
    if (!match) return null

    const [, dd, mm, yyyy] = match
    const date = new Date(Number(yyyy), Number(mm) - 1, Number(dd))

    const valid = date.getFullYear() === Number(yyyy) && date.getMonth() === Number(mm) - 1 && date.getDate() === Number(dd)
    return valid && Number(yyyy) >= 1900 && Number(yyyy) <= 2100 ? `${yyyy}-${mm}-${dd}` : null
}

function mask(raw) {
    const digits = String(raw || '').replace(/\D/g, '').slice(0, 8)

    if (digits.length <= 2) return digits
    if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`
}

const text = ref(toBr(props.value))
const isoValue = computed(() => toIso(props.value))

watch(() => props.value, (value) => {
    // Nao apaga o que o usuario esta digitando quando o valor emitido volta do pai.
    if (toIso(value) !== brToIso(text.value)) {
        text.value = toBr(value)
    }
})

function onInput(raw) {
    const masked = mask(raw)
    text.value = masked

    // Se a mascara nao mudou o texto (ex.: letra digitada), o Vuetify nao re-renderiza
    // o input - forca o valor mascarado no campo.
    if (field.value && field.value.lazyValue !== masked) {
        field.value.lazyValue = masked
    }

    if (masked === '') {
        emit('input', null)
        return
    }

    const iso = brToIso(masked)
    if (iso) emit('input', iso)
}

// Texto incompleto ou data invalida ao sair do campo: volta para a ultima data valida.
function onBlur() {
    if (text.value !== '' && !brToIso(text.value)) {
        text.value = toBr(props.value)
        if (field.value) field.value.lazyValue = text.value
    }
}

function onPick(iso) {
    text.value = toBr(iso)
    emit('input', iso)
    menu.value = false
}
</script>
